import { useMemo } from 'react';
import {
  Paper,
  Group,
  Grid,
  Select,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { IconFilter, IconUser } from '@tabler/icons-react';
import type { AdminInitiativeLead } from '../../api/admin';
import { useGroup } from '../../contexts/GroupContext';
import type { AdminMilestoneView } from '../../stores/adminMilestoneViewStore';
import {
  buildSubawardeeLabelLookup,
  filterSubawardeeIds,
  getSubawardeeDisplayLabel,
} from '../../utils/adminSubawardeeUtils';
import { filterLeadsForComponentProject } from '../../utils/initiativeLeadUtils';
import { AdminSubawardeeFilterSelect } from './AdminSubawardeeFilterSelect';

export const milestoneFilterInputStyles = (theme: any) => {
  const focusBorder = theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5];
  return {
    label: { fontSize: theme.fontSizes.xs, fontWeight: 500, marginBottom: 4 },
    input: {
      '&:focus, &:focus-within': { borderColor: focusBorder, boxShadow: 'none', outline: 'none' },
    },
    item: {
      whiteSpace: 'normal' as const,
      wordWrap: 'break-word' as const,
      wordBreak: 'break-word' as const,
      lineHeight: '1.4',
      padding: '8px 12px',
      minHeight: 'auto',
    },
    dropdown: { maxWidth: 400 },
  };
};

export const milestoneFilterStatusStyles = (theme: any) => ({
  ...milestoneFilterInputStyles(theme),
  dropdown: { maxWidth: 300 },
});

export interface AdminMilestonesFiltersProps {
  view: AdminMilestoneView;
  onViewChange: (view: AdminMilestoneView) => void;
  /** Navbar component project — scopes sub-awardee options on the sub-awardee tab. */
  componentProject: string | null;
  subawardee: string | null;
  onSubawardeeChange: (value: string | null) => void;
  initiativeLeadFilter: string | null;
  onInitiativeLeadFilterChange: (value: string | null) => void;
  selectedObjective: string | null;
  onSelectedObjectiveChange: (value: string | null) => void;
  selectedStatus: string | null;
  onSelectedStatusChange: (value: string | null) => void;
  objectiveOptions: { value: string; label: string }[];
  statusOptions: { value: string; label: string }[];
  initiativeLeads: AdminInitiativeLead[];
  detailFiltersDisabled?: boolean;
}

export function AdminMilestonesFilters({
  view,
  onViewChange,
  componentProject,
  subawardee,
  onSubawardeeChange,
  initiativeLeadFilter,
  onInitiativeLeadFilterChange,
  selectedObjective,
  onSelectedObjectiveChange,
  selectedStatus,
  onSelectedStatusChange,
  objectiveOptions,
  statusOptions,
  initiativeLeads,
  detailFiltersDisabled = false,
}: AdminMilestonesFiltersProps) {
  const { availableGroups } = useGroup();
  const labelById = useMemo(
    () => buildSubawardeeLabelLookup(availableGroups),
    [availableGroups]
  );

  const leadOptions = useMemo(
    () =>
      filterLeadsForComponentProject(initiativeLeads, componentProject, {
        view,
        subawardee,
      }).map((lead) => ({
        value: lead.id,
        label: lead.name,
      })),
    [initiativeLeads, componentProject, view, subawardee]
  );

  const subawardeeSelectData = useMemo(() => {
    const allowedIds = filterSubawardeeIds(initiativeLeads, {
      componentProjectId: componentProject,
      initiativeLeadId: initiativeLeadFilter,
    });

    return allowedIds
      .map((id) => ({
        value: id,
        label: getSubawardeeDisplayLabel(id, labelById) ?? id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [initiativeLeads, componentProject, initiativeLeadFilter, labelById]);

  const detailFilters = (
    <>
      {leadOptions.length > 0 && (
        <Grid.Col span={6} xs={12} sm={6}>
          <Select
            icon={<IconUser size={16} />}
            label="Initiative lead"
            placeholder="All leads"
            data={leadOptions}
            value={initiativeLeadFilter}
            onChange={onInitiativeLeadFilterChange}
            clearable
            searchable
            size="sm"
            styles={milestoneFilterInputStyles}
            maxDropdownHeight={300}
          />
        </Grid.Col>
      )}
      <Grid.Col span={6} xs={12} sm={6}>
        <Select
          label="Objective"
          placeholder="All objectives"
          data={objectiveOptions}
          value={selectedObjective}
          onChange={onSelectedObjectiveChange}
          disabled={detailFiltersDisabled}
          size="sm"
          styles={milestoneFilterInputStyles}
          maxDropdownHeight={300}
        />
      </Grid.Col>
      <Grid.Col span={6} xs={12} sm={6}>
        <Select
          label="Status"
          placeholder="All statuses"
          data={statusOptions}
          value={selectedStatus}
          onChange={onSelectedStatusChange}
          disabled={detailFiltersDisabled}
          size="sm"
          styles={milestoneFilterStatusStyles}
          maxDropdownHeight={300}
        />
      </Grid.Col>
    </>
  );

  return (
    <Stack spacing="md" mb="md">
      <SegmentedControl
        value={view}
        onChange={(value) => onViewChange(value as AdminMilestoneView)}
        data={[
          { label: 'Component project', value: 'component' },
          { label: 'Sub-awardee', value: 'subawardee' },
        ]}
        fullWidth
        sx={(theme) => ({
          '@media (max-width: 768px)': {
            margin: theme.spacing.sm,
            marginBottom: 0,
          },
        })}
      />

      <Paper
        radius="md"
        p="md"
        sx={(theme) => ({
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
          border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
          '@media (max-width: 768px)': {
            margin: theme.spacing.sm,
            padding: theme.spacing.sm,
          },
        })}
      >
        <Group spacing={6} mb="sm">
          <IconFilter size={15} style={{ opacity: 0.55 }} />
          <Text size="xs" weight={600} tt="uppercase" color="dimmed" sx={{ letterSpacing: 0.5 }}>
            Filters
          </Text>
        </Group>

        {view === 'component' ? (
          <Grid gutter="sm">{detailFilters}</Grid>
        ) : (
          <Grid gutter="sm">
            <Grid.Col span={6} xs={12} sm={6}>
              <AdminSubawardeeFilterSelect
                value={subawardee}
                onChange={onSubawardeeChange}
                data={subawardeeSelectData}
                size="sm"
                styles={milestoneFilterInputStyles}
              />
            </Grid.Col>
            {detailFilters}
          </Grid>
        )}
      </Paper>
    </Stack>
  );
}
